import React from 'react';
import { convertNodeToElement, type Transform } from 'react-html-parser';
import { useSelector } from 'react-redux';

import type { ImmutableRootState, ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import { Button, ParagraphsRenderer } from '../../../components';
import { COOKIE_EDIT_URL } from '../../../config/constants';
import useClasses from '../../../hooks/useClasses';
import useCookie from '../../../hooks/useCookie';
import type { WysiwygFieldDomElement } from '../../../types';
import type { NodeBasicPage as Type } from '../../../types/uncinc_profile/nodes';

import './style.scss';

interface HeroProps {
  title: string;
  body?: string;
  buildClass: ReturnType<typeof useClasses>['buildClass'];
}

const Hero: React.FC<HeroProps> = ({ title, body, buildClass }) => {
  const { updateConsent } = useCookie();

  return (
    <>
      <div className={buildClass('title')}>
        <h1>{title}</h1>
      </div>

      {body.length > 0 && (
        <div className={buildClass('body')}>
          <WysiwygField
            property="schema:text"
            html={body}
            // eslint-disable-next-line react/no-unstable-nested-components
            customElementHandler={(
              node: WysiwygFieldDomElement,
              index: number,
              transform?: Transform,
            ) => {
              switch (node.name) {
                // All anchor tags.
                case 'a': {
                  if (node.attribs?.href?.match(new RegExp(`${COOKIE_EDIT_URL}$`))) {
                    return (
                      <Button secondary onClick={updateConsent}>
                        {node.children?.[0]?.data}
                      </Button>
                    );
                  }

                  return convertNodeToElement(node, index, transform);
                }

                default:
                  return null;
              }
            }}
          />
        </div>
      )}
    </>
  );
};

export interface BasicPageProps {
  entity: ImmutableEntity<Type>;
}

const NodeBasicPage: React.FC<BasicPageProps> = ({ entity }) => {
  const { buildClass, joinClasses } = useClasses('NodeBasicPage');

  const isLoggedIn = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['login', 'loggedIn'], null));

  const basicPage = entity.toJS();
  const title = basicPage.title[0].value;
  const body = basicPage.body?.[0]?.processed || '';
  const paragraphs = basicPage.field_paragraphs;

  if (!isLoggedIn) {
    return (
      <Container>
        <div className="row">
          <div className={joinClasses(buildClass(), 'col-12 col-md-10 col-lg-8 offset-md-1 offset-lg-2 py-8 py-lg-12')}>
            <Hero title={title} body={body} buildClass={buildClass} />
            <ParagraphsRenderer paragraphs={paragraphs} />
          </div>
        </div>
      </Container>
    );
  }

  return (
    <div className={joinClasses(buildClass(), 'col-12 col-md-8 offset-md-2 offset-lg-1 py-8 py-lg-12')}>
      <Hero title={title} body={body} buildClass={buildClass} />
      <ParagraphsRenderer paragraphs={paragraphs} />
    </div>
  );
};

export default NodeBasicPage;
